import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import { isoDay, isSuperAdmin, leggiImporto, type Entity } from '../../features/admin/adminApi';

/**
 * Movimento in contanti (incasso o uscita): solo l'essenziale.
 * Controparte, descrizione, importo, data, già incassato/pagato (default sì).
 * L'entità (Masamune / personale) la sceglie solo il Super Admin.
 * Salva con Ctrl/Cmd+Invio.
 */
export function NewCashEntryModal({ onClose, direction = 'IN' }: { onClose: () => void; direction?: 'IN' | 'OUT' }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const isIn = direction === 'IN';
  const [form, setForm] = useState({
    counterparty: '',
    description: '',
    amount: '',
    date: isoDay(),
    done: true,
    entity: 'MASAMUNE' as Entity,
  });
  // "1.300,00" vale 1300 (prima diventava NaN e il tasto restava spento senza spiegazione).
  const amount = leggiImporto(form.amount);

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/admin/cash-entries', {
      direction,
      counterparty: form.counterparty.trim(),
      description: form.description.trim() || null,
      amountNet: amount,
      vatRate: 0,
      amountGross: amount,
      expectedDate: form.date,
      status: form.done ? 'PAGATO' : 'PREVISTO',
      paidAt: form.done ? form.date : null,
      method: 'CONTANTI',
      entity: form.entity,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok(isIn ? 'Incasso in contanti registrato' : 'Uscita in contanti registrata', `${form.counterparty} · € ${fmtEur(amount, true)}`);
      onClose();
    },
    onError: (err) => toast.alert(isIn ? 'Incasso in contanti non salvato' : 'Uscita in contanti non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.counterparty.trim() && Number.isFinite(amount) && amount > 0 && !!form.date && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title={isIn ? 'Nuovo incasso in contanti' : 'Nuova uscita in contanti'}
      kicker={isIn ? "Fatture" : "Spese"}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            {isIn ? "Registra l'incasso" : "Registra l'uscita"} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label={isIn ? 'Chi ci paga' : 'A chi paghiamo'} required span={12}>
          <input className="inp" autoFocus placeholder={isIn ? 'Cliente' : 'Fornitore, persona'} value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} />
        </Field>
        <Field label="Importo in contanti" required span={6}
          hint={form.amount.trim() && !(amount > 0) ? 'Scrivi solo il numero, per esempio 1.300,00' : undefined}>
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label={isIn ? "Data dell'incasso" : 'Data del pagamento'} required span={6}>
          <input className="inp mono" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="Per cosa" span={12} hint="Facoltativo">
          <input className="inp" placeholder={isIn ? 'Es. saldo sito, servizio una tantum' : 'Es. pulizie settembre, manutenzione'} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label={isIn ? 'I soldi sono già arrivati?' : 'I soldi sono già usciti?'} span={12}>
          <div className="seg-control">
            <button type="button" aria-pressed={form.done} onClick={() => setForm({ ...form, done: true })}>Sì, già fatto</button>
            <button type="button" aria-pressed={!form.done} onClick={() => setForm({ ...form, done: false })}>No, è previsto</button>
          </div>
        </Field>
        {isSuperAdmin(me) && (
          <Field label={isIn ? "Di chi è l'incasso" : "Di chi è la spesa"} span={12}>
            <div className="seg-control">
              <button type="button" aria-pressed={form.entity === 'MASAMUNE'} onClick={() => setForm({ ...form, entity: 'MASAMUNE' })}>Di Masamune</button>
              <button type="button" aria-pressed={form.entity === 'PERSONALE_ANDREA'} onClick={() => setForm({ ...form, entity: 'PERSONALE_ANDREA' })}>Di Andrea</button>
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}
